import { Card, Dialog, DialogContent, DialogTitle, DialogActions, Typography, Box, Grid, useTheme, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { allVariantsById } from "../APIs/VariantsAPI";
import { getAllCategorie } from '../APIs/CategoriesAPI';
import { getAllSottocategorie } from '../APIs/SubCategoriesAPI';
import { ReactComponent as DefaultPlaceHolder } from '../images/DefaultPlaceHolder.svg';

const ModalVariant = ({ open, onClose, product, onSelect, selectedVariantIds = [] }) => {
  const [variants, setVariants] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [sottoCategorie, setSottocategorie] = useState([]);
  const { t } = useTranslation();
  const theme = useTheme();
  const contextTP = theme.palette;

  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');

  useEffect(() => {
    if (!open || !product?.id) return;

    const productVariants = Array.isArray(product?.variants) ? product.variants : [];
    if (productVariants.length > 0) {
      setVariants(productVariants);
      return;
    }

    allVariantsById(product.id, token, tenant)
      .then((variantsData) => setVariants(Array.isArray(variantsData) ? variantsData : []))
      .catch((err) => {
        console.error("Errore nel fetch varianti:", err);
        setVariants([]);
      });
  }, [open, product, tenant, token]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categorieData = await getAllCategorie(token, tenant);
        setCategorie(categorieData);
        const subCategorieData = await getAllSottocategorie(token, tenant);
        setSottocategorie(subCategorieData);
      } catch (err) {
        console.error('Errore nel caricamento categorie:', err);
      }
    };
    fetchCategories();
  }, [tenant, token]);

  const findCategories = (categoryId, subcategoryId) => {
    const category = categorie.find(cat => cat.id === subcategoryId);
    const subcategory = sottoCategorie.find(sub => sub.id === categoryId);
    return { category, subcategory };
  };

  // ── FIX: HECTOGRAM / KILOGRAM Prisma enum
  const getPriceLabel = (price) => {
    if (!product.pricePerWeight) return `€${price.toFixed(2)}`;
    const unit = product.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg';
    return `€${price.toFixed(2)}/${unit}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("modalVariant.title")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} paddingBottom="25px" sx={{ height: '450px', overflowY: 'auto' }}>
          {variants.map((variant) => (
            <Grid item xs={16} sm={8} md={4} key={variant.id}>
              {(() => {
                const isSelected = selectedVariantIds.map((id) => Number(id)).includes(Number(variant.id));
                return (
              <Card sx={{
                color: contextTP.mode === 'light' ? '#131313' : '#FAFAFA',
                display: 'flex', padding: '1px', flexDirection: 'column',
                alignItems: 'flex-start', borderRadius: '8px',
                background: contextTP.mode === 'light' ? '#FAFAFA' : '#333333',
                boxShadow: isSelected ? '0 6px 24.5px 0 rgba(212, 114, 0, 0.20)' : '0 6px 24.5px 0 rgba(0, 0, 0, 0.16)',
                border: '3px solid',
                borderColor: isSelected ? '#D47200' : 'transparent',
              }}>

                {/* Original image block — DefaultPlaceHolder restored */}
                <Box sx={{
                  display: 'flex', width: '100%', height: '192px',
                  alignItems: 'center', aspectRatio: '41/24',
                  overflow: 'hidden', justifyContent: 'center',
                }}>
                  {variant.photoPath ? (
                    <img
                      src={variant.photoPath}
                      alt=" "
                      style={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <DefaultPlaceHolder
                      style={{ width: '75%', height: '75%', color: '#131313' }}
                      fill={'#131313'}
                    />
                  )}
                </Box>

                <Box sx={{
                  display: 'flex', padding: '16px', flexDirection: 'column',
                  alignItems: 'flex-start', gap: '10px', alignSelf: 'stretch',
                  borderTop: `1px solid ${contextTP.mode === 'light' ? '#E0E0E0' : '#4F4F4F'}`,
                  background: contextTP.mode === 'light' ? '#FAFAFA' : '#333333',
                }}>
                  <Typography variant="h5" component="span" sx={{
                    textAlign: 'center', fontSize: '24px', fontStyle: 'normal',
                    fontWeight: 700, lineHeight: '28px', maxWidth: 262,
                    whiteSpace: 'nowrap', overflow: 'hidden', position: 'relative',
                    '& span': { display: 'inline-block', paddingLeft: '100%', animation: 'marquee 8s linear infinite' },
                    '@keyframes marquee': { '0%': { transform: 'translateX(0)' }, '100%': { transform: 'translateX(-100%)' } },
                  }}>
                    {variant.name}&nbsp;&nbsp;&nbsp;
                  </Typography>

                  <Typography variant="body2" color="textSecondary" sx={{
                    color: contextTP.mode === 'light' ? '#303030' : '#B5B5B5',
                    width: '253px', height: '40px', fontSize: '14px',
                    fontWeight: 400, lineHeight: '20px',
                  }}>
                    {(() => {
                      const { category, subcategory } = findCategories(variant.subcategoryId, variant.categoryId);
                      if (category && subcategory) return `${category.name} - ${subcategory.name}`;
                      if (category) return category.name;
                      return t('NoCategory');
                    })()}
                  </Typography>

                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="body1" sx={{
                      color: contextTP.mode === 'light' ? '#CC4B00' : '#F9934B',
                      textAlign: 'center', fontSize: '24px', fontStyle: 'normal',
                      fontWeight: 700, lineHeight: '28px',
                    }}>
                      {getPriceLabel(variant.price)}
                    </Typography>

                    <Button
                      variant="contained"
                      sx={{
                        display: 'flex', height: '48px', minWidth: '60px',
                        py: '10px', px: '19px', justifyContent: 'center',
                        alignItems: 'center', gap: '10px', borderRadius: '8px',
                        background: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                      }}
                      onClick={() => { onSelect(variant); }}
                    >
                      <Typography color={contextTP.mode === 'light' ? '#131313' : '#FBFBFB'}>
                        {isSelected ? t("selected") : t("select")}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </Card>
                );
              })()}
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("confirmOperation.confirm")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalVariant;
